import React, { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { blockService } from "../../../store/actions";
import useCommon from "../../../common/hooks/useCommon";
import {
  convertSmallerUnit,
  formatNumber,
  formatNumberWithCommas,
  getTimeLang,
  formatUnit,
  formatValue,
  toGwei,
  toXDCNumber,
} from "../../../common/utils/function";

import BTooltip from "../../../components/b-tooltip";
import BSkeleton from "../../../components/b-skeleton";
import BGauge from "../../../components/b-gauge";
import { useTranslation } from "react-i18next";
import BCopy from "../../../components/b-copy";

interface BlockDetailsData {
  height: any;
  epoch: any;
  timestamp: any;
  tx_count: any;
  withdrawals_count: any;
  hash: any;
  parent_hash: any;
  miner: any;
  size: any;
  gas_used: any;
  gas_used_percentage: any;
  gas_limit: any;
  base_fee_per_gas: any;
  burnt_fees: any;
  extraData: any;
  difficulty: any;
  nonce: any;
  gas_target_percentage: any;
  rewards: any;
  reward: any;
  total_difficulty: any;
  tx_fees: any;
  transaction_count: any;
  contract_internal_transactions: any;
  uncles_hashes: any;
}

const BlockDetailsOverview: FC<any> = (props) => {
  const { block } = useParams();
  const navigate = useNavigate();
  const { getTime, getXDC, get0x } = useCommon();

  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const [isGettingError, setIsGettingError] = useState(false);
  const [stats, setStats] = useState<BlockDetailsData>({
    height: "",
    epoch: null,
    timestamp: "",
    tx_count: 0,
    withdrawals_count: 0,
    hash: "",
    parent_hash: "",
    miner: {},
    size: "",
    gas_used: "",
    gas_used_percentage: 0,
    gas_limit: "",
    base_fee_per_gas: "",
    burnt_fees: "",
    extraData: "",
    difficulty: "",
    total_difficulty: "",
    nonce: "",
    gas_target_percentage: 0,
    rewards: [],
    reward: 0,
    tx_fees: 0,
    transaction_count: 0,
    contract_internal_transactions: 0,
    uncles_hashes: [],
  });

  // const getBlock = async () => {
  //     try {
  //         setLoading(true);
  //         blockService
  //             .getBlock(block)
  //             .then((response) => {
  //                 setStats(response);
  //                 setLoading(false);
  //             })
  //             .catch((e) => setIsGettingError(true));
  //     } catch (err) {
  //         console.log(err);
  //     }
  // };
  const getBlock = async () => {
    try {
      setLoading(true);
      const response = await blockService.getBlock(block);
      if (!response.hash || !response.timestamp) {
        // Redirect to countdown if the block is not mined
        navigate(`/block/countdown/${block}`);
      } else {
        setStats(response);
      }
      setLoading(false);
    } catch (e) {
      console.error("Error fetching block:", e);
      setIsGettingError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBlock();
  }, []);

  useEffect(() => {
    if (isGettingError) {
      props?.handleGetError(true);
    }
  }, [isGettingError]);

  const handleNext = () => {
    const nextBlock = parseInt(block || "0") + 1;
    navigate(`/block/${nextBlock}`);
    window.location.reload();
  };

  const handlePrev = () => {
    const prevBlock = Math.max(parseInt(block || "0") - 1, 0);
    navigate(`/block/${prevBlock}`);
    window.location.reload();
  };
  return (
    <React.Fragment>
      <div className="bs-card mb-3">
        <div className="bs-card-body py-3 px-4">
          <div className="bs-list">
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "Also known as Block Number. The block height, which indicates the length of the blockchain, increases after the addition of the new block."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Block Height")}:
              </div>
              <div className="bs-list-content">
                {block}{" "}
                <button
                  className="  bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium px-1 rounded-2 m-lg-1"
                  onClick={handlePrev}
                >
                  {" "}
                  <BTooltip text={`${t("View Previous Block")}`}>
                    <i className="bi bi-chevron-left "> </i>
                  </BTooltip>
                </button>{" "}
                <button
                  className=" bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium px-1 rounded-2 "
                  onClick={handleNext}
                >
                  <BTooltip text={`${t("View Next Block")}`}>
                    <i className="bi bi-chevron-right "> </i>
                  </BTooltip>
                </button>
              </div>
            </div>
            {/* <div className="bs-list-row">
                            <div className="bs-list-title">
                                <BTooltip text={`${t("The finality status of the block.")}`}>
                                    <i className="bi bi-question-circle me-2"></i>
                                </BTooltip>
                                {t("Status")}:
                            </div>
                            <div className="bs-list-content">
                                {loading ? (
                                    <BSkeleton/>
                                ) : (
                                    <React.Fragment>
                                        <BTooltip text="This block is finilized.">
                                            {t("ToDo")}:{" "}
                                            <span className={`badge text-bg-success`}>
                                                {t("Finalized")}
                                            </span>
                                        </BTooltip>
                                    </React.Fragment>
                                )}
                            </div>
                        </div> */}
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "The date and time at which a block is produced."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Timestamp")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    <i className="bi bi-clock"></i>{" "}
                    {getTimeLang(stats?.timestamp, t)} |{" "}
                    {getTime(
                      stats?.timestamp,
                      "MMM DD YYYY hh:mm:ss A (Z UTC)",
                      false
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t("Slot and epoch this block is proposed on.")}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Proposed On")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {t("Block proposed on epoch")}{" "}
                    {/* <Link
                                            to={`/epochs/${Math.ceil(Number(stats?.height) / 900)}`}
                                        > */}
                    {Math.ceil(Number(stats?.height) / 900)}
                    {/* </Link> */}
                  </React.Fragment>
                )}
              </div>
            </div>
            {stats?.tx_count > 0 && (
              <div className="bs-list-row">
                <div className="bs-list-title">
                  <BTooltip
                    text={`${t(
                      "The number of transactions in the block. Internal transaction is transactions as a result of contract execution that involves Ether value."
                    )}`}
                  >
                    <i className="bi bi-question-circle me-2"></i>
                  </BTooltip>
                  {t("Transactions")}:
                </div>
                <div className="bs-list-content">
                  {loading ? (
                    <BSkeleton />
                  ) : (
                    <React.Fragment>
                      <Link to={`/txs?block=${block}`}>
                        {stats?.tx_count} {t("Transactions")}
                      </Link>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}

            {stats?.transaction_count >= 1 && (
              <div className="bs-list-row">
                <div className="bs-list-title">
                  <BTooltip
                    text={`${t("Number of Transactions in this block.")}`}
                  >
                    <i className="bi bi-question-circle me-2"></i>
                  </BTooltip>
                  {t("Transactions")}:
                </div>
                <div className="bs-list-content">
                  {loading ? (
                    <BSkeleton />
                  ) : (
                    <React.Fragment>
                      <Link to={`/txs?block=${block}`}>
                        <BTooltip text={`${t("Click to View Transactions")}`}>
                          {stats?.transaction_count}{" "}
                          {t(
                            stats?.transaction_count === 1
                              ? "transaction"
                              : "transactions"
                          )}
                        </BTooltip>{" "}
                      </Link>{" "}
                      {/* and{" "}
                      <Link to={`/txsInternal?block=${block}`}>
                        <BTooltip
                          text={`${t("Click to View Internal Transactions")}`}
                        >
                          {stats?.contract_internal_transactions}{" "}
                          {t("contract internal transactions")}
                        </BTooltip>{" "}
                      </Link> */}
                      in this block.
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}
            <hr />
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "Address receiving fees from transactions in this block."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Fee Recipient")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {/* {t("ToDo")}: {t("rsync-builder in 12 secs")} */}
                    <Link to={`/address/${stats?.miner?.hash}`}>
                      {stats?.miner?.ens_domain_name || stats?.miner?.hash}
                    </Link>
                    <BCopy value={stats?.miner?.hash}></BCopy>
                  </React.Fragment>
                )}
              </div>
            </div>
            {stats?.rewards?.length > 0 && (
              <div className="bs-list-row">
                <div className="bs-list-title">
                  <BTooltip
                    text={`${t(
                      "For each block, the block producer is rewarded with a finite amount of Ether on top of the fees paid for all transactions in the block."
                    )}`}
                  >
                    <i className="bi bi-question-circle me-2"></i>
                  </BTooltip>
                  {t("Block Reward")}:
                </div>
                <div className="bs-list-content">
                  {loading ? (
                    <BSkeleton />
                  ) : (
                    <React.Fragment>
                      <span className="text-muted">
                        {convertSmallerUnit(stats?.rewards?.[0]?.reward ?? 0) ??
                          0}{" "}
                        {process.env.REACT_APP_BASE_UNIT} (0 + &nbsp;
                        <BTooltip text={t("Transaction Fee")}>
                          {convertSmallerUnit(stats?.tx_fees)} &nbsp;
                        </BTooltip>
                        -{" "}
                        <BTooltip text={t("Burnt Fees")}>
                          {convertSmallerUnit(stats?.burnt_fees)}
                        </BTooltip>
                        )
                      </span>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip text="Total difficulty of the chain until this block.">
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                Total Difficulty:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {formatNumberWithCommas(stats?.total_difficulty ?? 0)}
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "The block size is actually determined by the block's gas limit."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Size")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {formatNumber(stats?.size ?? 0)} {t("bytes")}
                  </React.Fragment>
                )}
              </div>
            </div>
            <hr />
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "The total gas used in the block and its percentage of gas filled in the block."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Gas Used")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <div className="d-flex align-items-center">
                    <span>
                      {formatNumber(stats?.gas_used ?? 0)}
                      <span className="text-muted small">
                        ({formatNumber(stats?.gas_used_percentage ?? 0, 2)}%)
                      </span>{" "}
                      &nbsp;{" "}
                    </span>
                    <span className="ms-2">
                      <BGauge
                        percentage={stats?.gas_used_percentage ?? 0}
                        color={
                          stats?.gas_used_percentage > 0 ? "#00a186" : "#dc3545"
                        }
                      />
                    </span>
                    &nbsp;
                    <span
                      className={
                        formatValue(stats?.gas_target_percentage).startsWith(
                          "+"
                        )
                          ? "text-success ms-2"
                          : "text-danger ms-2"
                      }
                    >
                      {formatValue(stats?.gas_target_percentage ?? 0)}%{" "}
                      {t("Gas Target")}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "Total gas limit provided by all transactions in the block."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Gas Limit")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {formatNumber(stats?.gas_limit ?? 0)}
                  </React.Fragment>
                )}
              </div>
            </div>
            {/* <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={`${t(
                    "Post-London Upgrade, this represents the minimum gas Used multiplier required for a tx to be included in a block."
                  )}`}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Base Fee Per Gas")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {formatUnit(toXDCNumber(stats?.base_fee_per_gas ?? 0, 18))}{" "}
                    ({toGwei(stats?.base_fee_per_gas ?? 0)} Gwei)
                  </React.Fragment>
                )}
              </div>
            </div> */}
            {stats?.burnt_fees > 0 && (
              <div className="bs-list-row">
                <div className="bs-list-title">
                  <BTooltip
                    text={`${t(
                      "Post-London Upgrade, this represents the part of the tx fee that is burnt: baseFeePerGas * gasUsed."
                    )}`}
                  >
                    <i className="bi bi-question-circle me-2"></i>
                  </BTooltip>
                  {t("Burnt Fees")}:
                </div>
                <div className="bs-list-content">
                  {loading ? (
                    <BSkeleton />
                  ) : (
                    <React.Fragment>
                      🔥 {formatUnit(toXDCNumber(stats?.burnt_fees ?? 0, 18))}
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bs-card mb-3">
        <div className="bs-card-body py-3 px-4">
          <div className="bs-list">
            {showMore && (
              <React.Fragment>
                <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip
                      text={`${t(
                        "The hash of the block header of the current block."
                      )}`}
                    >
                      <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {t("Hash")}:
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>{get0x(stats?.hash)}</React.Fragment>
                    )}
                  </div>
                </div>
                <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip
                      text={`${t(
                        "The hash of the block from which this block was generated, also known as its parent block."
                      )}`}
                    >
                      <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {t("Parent Hash")}:
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>
                        <Link to={`/block/${Number(stats?.height) - 1}`}>
                          {get0x(stats?.parent_hash)}
                        </Link>
                      </React.Fragment>
                    )}
                  </div>
                </div>
                <div className="bs-list-row">
                  <div className="bs-list-title">
                    {stats?.uncles_hashes?.length > 0 && (
                      <>
                        <BTooltip
                          text={`${t(
                            "The mechanism which XDC Javascript RLP encodes and empty String."
                          )}`}
                        >
                          <i className="bi bi-question-circle me-2"></i>
                        </BTooltip>
                        {t("Sha3Uncles")}:
                      </>
                    )}
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>
                        {stats?.uncles_hashes?.length > 0 && (
                          <React.Fragment>
                            {get0x(stats.uncles_hashes[0])}
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    )}
                  </div>
                </div>
                {/* <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip text={`${t("The root of the state trie.")}`}>
                      <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {t("StateRoot")}:
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>
                        {t("ToDo")}:{" "}
                        <Link to={`/block/${Number(stats?.height) - 1}`}>
                          {get0x(stats?.parent_hash)}
                        </Link>
                      </React.Fragment>
                    )}
                  </div>
                </div>
                <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip
                      text={`${t("Block header withdrawal root hash.")}`}
                    >
                      <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {t("WithdrawalsRoot")}:
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>
                        {t("ToDo")}:{" "}
                        <Link to={`/block/${Number(stats?.height) - 1}`}>
                          {get0x(stats?.parent_hash)}
                        </Link>
                      </React.Fragment>
                    )}
                  </div>
                </div> */}
                {/* <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip
                      text={`${t(
                        "Block nonce is a value used during mining to demonstrate proof of work for a block."
                      )}`}
                    >
                      <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {t("Nonce")}:
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>{stats?.nonce}</React.Fragment>
                    )}
                  </div>
                </div> */}
                <hr />
              </React.Fragment>
            )}
            <div className="bs-list-row">
              <div className="bs-list-title">{t("More Details")}:</div>
              <div className="bs-list-content">
                <a onClick={() => setShowMore(!showMore)} className="bs-cp">
                  {showMore
                    ? `- ${t("Click to show less")}`
                    : `+ ${t("Click to show more")}`}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BlockDetailsOverview;
